import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { type Entity } from "@/admin/types";
import { usePaginateQuery } from "@/hooks";
import { adminApi } from "@/lib/api";
import { type Paginate, type SearchRequest } from "@/types";

export type EntityList = Paginate<Entity>;

export const entities = createQueryKeys("admin-entities", {
  all: (page?: number, filter?: SearchRequest) => [{ page, filter }],
  one: (id?: number) => [id],
});

const include = "columns,rows";

export const fetchEntitiesQueryOptions = (
  page: number = 0,
  search?: SearchRequest,
) =>
  queryOptions({
    ...entities.all(page, search),
    queryFn: () =>
      adminApi
        .get<EntityList>("entities", {
          params: {
            page,
            ...search,
          },
        })
        .then((response) => response.data),
  });

export const useFetchEntities = (search?: SearchRequest) =>
  usePaginateQuery((page) => fetchEntitiesQueryOptions(page, search));

export const fetchEntityQueryOptions = (id?: number) =>
  queryOptions({
    ...entities.one(id),
    queryFn: () =>
      adminApi
        .get<Entity<true, true>>(`entities/${id}`, { params: { include } })
        .then((response) => response.data),
    enabled: id !== undefined,
  });

export const useFetchEntity = (id?: number) =>
  useQuery(fetchEntityQueryOptions(id));

export interface EntityRequest {
  name: string | null;
  title: string;
  columns: Array<{ id?: number; name: string }>;
  rows: Array<{ id?: number; items: string[] }>;
}

export const useCreateEntity = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EntityRequest) =>
      adminApi.post("entities", data).then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: entities.all._def,
      });
    },
  });
};

export const useUpdateEntity = (id: number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: EntityRequest) =>
      adminApi.put(`entities/${id}`, data).then((response) => response.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: entities.all._def,
      });
      queryClient.invalidateQueries(entities.one(id));
    },
  });
};

export const useCreateOrUpdateEntity = (id: number | undefined) => {
  const create = useCreateEntity();
  const update = useUpdateEntity(id!);
  return id ? update : create;
};

export const useDeleteEntity = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => adminApi.delete(`entities/${id}`),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({
        queryKey: entities.all._def,
      });
      queryClient.invalidateQueries(entities.one(id));
    },
  });
};
